import { computed, DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { AssertService } from '@connect-services/client/services/assert.service';
import { HasPermissionsService } from '@connect-services/client/services/has-permissions.service';
import { ImmersiveViewUiService } from '@connect-services/client/services/immersive-view/immersive-view-ui.service';
import { SecurityService } from '@connect-services/client/services/security.service';
import { SubscriptionService } from '@connect-services/client/services/subscription.service';
import { PermissionAliasEnum } from '@connect-services/src/sdk/api';

const EXCLUDED_ROUTE_PATHS = [
    RoutePathEnum.EXT,
    RoutePathEnum.FIELD_INTERVENTION_CHANGE_SELECTION,
    RoutePathEnum.FIELD_INTERVENTION_CREATE,
    RoutePathEnum.SYNOPTIC,
    RoutePathEnum.SYNOPTIC_PLAN,
];

export enum ImmersiveViewErrorEnum {
    ACCESS_DENIED = 'access-denied',
    GOOGLE_QUOTA_EXHAUSTED = 'google-quota-exhausted',
    PANORAMA_NOT_FOUND = 'panorama-not-found',
    VIEWER_UNAVAILABLE = 'viewer-unavailable',
}

export interface ImmersiveViewPermissions {
    canNavigate: boolean;
    panoramaxPermission: boolean;
    streetViewPermission: boolean;
}

export enum ImmersiveViewServiceEnum {
    GOOGLE_STREET_VIEW = 'google-street-view',
    PANORAMAX = 'panoramax',
}

export interface ImmersiveViewPosition {
    lat: number;
    lng: number;
}

export const IMMERSIVE_VIEW_MARKER_HTML = `
    <img
        src="assets/images/icons/street-view-marker.svg"
        style="width: 40px; height: 40px;"
        id="street-view-marker"
    />
`;

@Injectable({
    providedIn: 'root',
})
export class ImmersiveViewService {
    public readonly canUseImmersiveView = computed<boolean>(() => {
        return this.canNavigate() && this.defaultImmersiveView() !== null;
    });
    public readonly error = signal<ImmersiveViewErrorEnum | null>(null);
    public readonly immersiveViewPermissions = computed<ImmersiveViewPermissions>(() => {
        return {
            canNavigate: this.canNavigate(),
            panoramaxPermission: this.canUsePanoramax(),
            streetViewPermission: this.canUseStreetView(),
        };
    });
    public readonly isImmersiveViewActive = signal(false);
    public readonly isLoading = signal<boolean>(false);
    public readonly isPermissionsLoading = signal<boolean>(true);
    public readonly markerPosition = signal<ImmersiveViewPosition | null>(null);
    public readonly requestedPosition = signal<ImmersiveViewPosition | null>(null);

    private readonly canNavigate = signal(false);
    private readonly canUsePanoramax = signal(false);
    private readonly canUseStreetView = computed(() =>
        this.hasPermissionsService.checkPermissions([PermissionAliasEnum.READ_GOOGLE_STREET_VIEW]),
    );
    private readonly currentImmersiveViewState = signal<ImmersiveViewServiceEnum | null>(null);
    private readonly defaultImmersiveView = computed<ImmersiveViewServiceEnum | null>(() => {
        const { panoramaxPermission, streetViewPermission } = this.immersiveViewPermissions();

        if (panoramaxPermission) {
            return ImmersiveViewServiceEnum.PANORAMAX;
        }

        if (streetViewPermission) {
            return ImmersiveViewServiceEnum.GOOGLE_STREET_VIEW;
        }

        return null;
    });

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly currentImmersiveView = this.currentImmersiveViewState.asReadonly();

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly destroyRef = inject(DestroyRef);
    private readonly hasPermissionsService = inject(HasPermissionsService);
    private readonly immersiveViewUiService = inject(ImmersiveViewUiService);
    private readonly router = inject(Router);
    private readonly securityService = inject(SecurityService);
    private readonly subscriptionService = inject(SubscriptionService);

    public constructor() {
        effect(() => {
            if (!this.securityService.meSignal()) {
                this.resetImmersiveView();

                return;
            }
            void this.checkPanoramaxPermissions();
        });

        this.checkAvailabilityRoute();
    }

    public canSelectImmersiveView(view: ImmersiveViewServiceEnum): boolean {
        const { canNavigate, panoramaxPermission, streetViewPermission } = this.immersiveViewPermissions();

        if (!canNavigate) {
            return false;
        }

        switch (view) {
            case ImmersiveViewServiceEnum.PANORAMAX:
                return !this.isPermissionsLoading() && panoramaxPermission;

            case ImmersiveViewServiceEnum.GOOGLE_STREET_VIEW:
                return streetViewPermission;

            default:
                return false;
        }
    }

    public openAtLocation(position: ImmersiveViewPosition): void {
        if (!this.canUseImmersiveView() || !this.isImmersiveViewActive()) {
            return;
        }

        const selectedView: ImmersiveViewServiceEnum | null = this.currentImmersiveView();

        const currentImmersiveView: ImmersiveViewServiceEnum | null =
            selectedView && this.canSelectImmersiveView(selectedView) ? selectedView : this.defaultImmersiveView();

        if (!currentImmersiveView) {
            return;
        }

        this.error.set(null);
        this.currentImmersiveViewState.set(currentImmersiveView);
        this.requestedPosition.set(position);
        this.markerPosition.set(position);
        this.immersiveViewUiService.isPanelOpened.set(true);
    }

    public resetImmersiveView(): void {
        this.requestedPosition.set(null);
        this.markerPosition.set(null);
        this.currentImmersiveViewState.set(null);
        this.isLoading.set(false);
        this.error.set(null);
        this.immersiveViewUiService.isPanelOpened.set(false);
    }

    public selectImmersiveView(view: ImmersiveViewServiceEnum): void {
        if (!this.canSelectImmersiveView(view) || this.currentImmersiveView() === view) {
            return;
        }

        this.error.set(null);
        this.isLoading.set(false);
        this.currentImmersiveViewState.set(view);
    }

    public toggleImmersiveView(): void {
        const isActive = this.isImmersiveViewActive();

        if (!isActive && !this.canUseImmersiveView()) {
            return;
        }

        if (isActive) {
            this.isImmersiveViewActive.set(false);
            this.resetImmersiveView();

            return;
        }

        this.currentImmersiveViewState.set(this.defaultImmersiveView());
        this.isImmersiveViewActive.set(true);
    }

    private checkAvailabilityRoute(): void {
        this.router.events
            .pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((): void => {
                this.updateImmersiveViewAvailabilityFromRoute();
            });

        this.updateImmersiveViewAvailabilityFromRoute();
    }

    private async checkPanoramaxPermissions(): Promise<void> {
        this.isPermissionsLoading.set(true);

        try {
            const subscription = await this.subscriptionService.getSubscription();

            this.canUsePanoramax.set(subscription.options?.hasPanoramax === true);
        } catch {
            this.canUsePanoramax.set(false);
        } finally {
            this.isPermissionsLoading.set(false);
        }
    }

    private getDeepestRouteChild(): ActivatedRoute {
        let route: ActivatedRoute = this.activatedRoute;
        while (route.firstChild) {
            route = route.firstChild;
        }

        return route;
    }

    private updateImmersiveViewAvailabilityFromRoute(): void {
        const activatedRoute: ActivatedRoute = this.getDeepestRouteChild();
        const currentRoutePathEnum: RoutePathEnum | undefined = activatedRoute.snapshot?.data?.['name'];

        if (currentRoutePathEnum !== undefined) {
            AssertService.isEnum<RoutePathEnum>(currentRoutePathEnum, RoutePathEnum);
        }

        const isDetailPanelOpen: boolean = this.router.url.includes('(detailPanel:');
        const canNavigate: boolean =
            currentRoutePathEnum !== undefined &&
            !EXCLUDED_ROUTE_PATHS.includes(currentRoutePathEnum) &&
            !isDetailPanelOpen;

        this.canNavigate.set(canNavigate);

        if (!canNavigate && this.isImmersiveViewActive()) {
            this.isImmersiveViewActive.set(false);
            this.resetImmersiveView();
        }
    }
}
